/**
 * Targeted bolt skill for Conductor enemies — windup with targeting line, then instant single-frame damage.
 * Damage applies for exactly one frame on entering the active phase.
 * The bolt has no travel time — it hits instantly at the target's position.
 */
import { PhasedSkillEffect } from '../PhasedSkillEffect';
import type { SkillPhase } from '../PhasedSkillEffect';
import type { EnemySkillEffect, EnemySkillMovement, EnemySkillDamageInfo } from './EnemySkillEffect';

export interface EnemyBoltConfig {
  boltDamage: number;
  boltRange: number;
  windupMs: number;
  recoveryMs: number;
  cooldownMs: number;
  attackType: string;
}

/** Ranged bolt attack that fires a projectile toward the target during the active phase. */
export class EnemyBoltSkillEffect extends PhasedSkillEffect implements EnemySkillEffect {
  readonly skillId: string;
  protected readonly phaseSequence = ['windup', 'active', 'recovery', 'cooldown'] as const;
  private readonly config: EnemyBoltConfig;

  /** Whether the damage frame has been consumed. Prevents multi-frame damage. */
  private _damageConsumed: boolean = false;

  constructor(skillId: string, config: EnemyBoltConfig) {
    super();
    this.skillId = skillId;
    this.config = config;
  }

  protected getPhaseDuration(phase: SkillPhase): number {
    switch (phase) {
      case 'windup': return this.config.windupMs;
      case 'active': return 50; // Brief active frame for damage + visual flash
      case 'recovery': return this.config.recoveryMs;
      case 'cooldown': return this.config.cooldownMs;
      default: return 0;
    }
  }

  protected onPhaseEnter(phase: SkillPhase): void {
    if (phase === 'active') {
      this._damageConsumed = false;
    }
  }

  get skillRange(): number { return this.config.boltRange; }

  /** True for exactly one frame — the first frame of active phase before damage is consumed. */
  isInDamagePhase(): boolean {
    return this.phase === 'active' && !this._damageConsumed;
  }

  /** Stand still during entire sequence. */
  getMovement(): EnemySkillMovement | null {
    if (this.phase === 'windup' || this.phase === 'active' || this.phase === 'recovery') {
      return { vx: 0, vy: 0 };
    }
    return null;
  }

  /** Returns bolt damage during the single damage frame, then marks as consumed. */
  getDamageInfo(): EnemySkillDamageInfo | null {
    if (!this.isInDamagePhase()) return null;
    this._damageConsumed = true;
    return {
      baseDamage: this.config.boltDamage,
      attackType: this.config.attackType,
      range: this.config.boltRange,
    };
  }
}
